/**
PW Tabs

Wrap around any number of ReactBase.Tabs components

Options (props)
- activeTabKay [string]: 'key' of the active child Tab
- flex [bool]: add flexbox classes to tabs

example configuration:
  <ReactBase.Tab onClicked={ myFuncRef }>Foo</ReactBase.Tab>

Some code borrowed/inspired by https://github.com/react-bootstrap/react-bootstrap/blob/master/src/NavItem.jsx

**/

import _ from 'lodash'
import React, { cloneElement } from 'react'
import T from 'prop-types'
import ReactDOM from 'react-dom'
import DimensionAware from '../hoc/DimensionAware'
import MenuItem from './MenuItem'
import ConfirmMenuItem from './ConfirmMenuItem'
import cx from 'classnames'
import { areComponentsEqual } from 'react-hot-loader'
import { generateId, stepFocus } from '../utils'

const INDICATOR_BASE_PX = 100 // matches base px size from CSS

const isDefinedCmpProp = (val, key) => Tabs.propTypes.hasOwnProperty(key)

class Tabs extends React.Component {
  static displayName = 'Tabs'

  static propTypes = {
    activeTabKey: T.any,
    children: T.any,
    className: T.string,
    flex: T.bool,
    radio: T.bool,
    vertical: T.bool,

    height: T.number,
    width: T.number
  }

  static defaultProps = {
    vertical: false,
    flex: false,
    className: ''
  }

  _id = generateId('tabs')

  constructor(props) {
    super(props)
    this._refs = {}
    const dir = this.props.vertical ? 'Y' : 'X'
    this.state = {
      activeRef: '',
      indicatorTransform: `translate(0) scale${dir}(0)`,
      radioTransform: `translate${dir}(0) scale(0)`,
      indicatorClassName: null
    }
  }

  componentDidMount() {
    _.defer(this._updateIndicatorPosition)
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.height !== this.props.height ||
      nextProps.width !== this.props.width
    ) {
      this._updateIndicatorPosition()
    }
  }

  componentDidUpdate() {
    this._updateIndicatorPosition()
  }

  componentWillUnmount() {
    this._isUnmounting = true
  }

  isActiveTab = childTab => {
    if (childTab.props.active) return true

    if (this.props.activeTabKey != null) {
      // coerce keys to strings since React does that internally
      if ('' + childTab.key === '' + this.props.activeTabKey) {
        return true
      }
    }

    return childTab.props.active
  }

  validateChild = childTab => {
    if (childTab.ref) {
      throw new Error(
        'PW ReactBase.Tabs: tabs override any passed refs with a duplicate of the key'
      )
    }

    if (!childTab.key) {
      throw new Error(
        'PW ReactBase.Tabs: key is required on all child MenuItems'
      )
    }
  }

  _bindTabRef = (key, el) => {
    this._refs[key] = el
  }

  _onKeyDown = e => {
    // Arrow keys step through tabs
    if (!e.isDefaultPrevented()) {
      const regex = this.props.vertical ? /(Up|Down)$/ : /(Left|Right)$/
      if (regex.test(e.key)) {
        stepFocus(ReactDOM.findDOMNode(this), /Up|Left/.test(e.key) ? -1 : 1)
        e.preventDefault()
      }
    }
  }

  processTabs = childTab => {
    if (
      !childTab ||
      (!areComponentsEqual(childTab.type, MenuItem) && !areComponentsEqual(childTab.type, ConfirmMenuItem))
    ) {
      return childTab
    }
    this.validateChild(childTab)
    const isActive = this.isActiveTab(childTab)
    const radio = this.props.radio
    return cloneElement(childTab, {
      args: childTab.props.args != null ? childTab.props.args : childTab.key, //default to passing the key as onClick arg
      active: isActive,
      key: childTab.key,
      ref: _.partial(this._bindTabRef, childTab.key),
      role: radio ? 'radio' : 'tab',
      'aria-controls': this._id
    })
  }

  getActiveEl = () => {
    return this.props.children.filter(child => child.props.active)
  }

  _updateIndicatorPosition = newActiveTabKey => {
    if (this._isUnmounting) return
    var position, indicatorClass, size
    var vertical = this.props.vertical
    var dir = vertical ? 'Y' : 'X'

    const activeKey = newActiveTabKey || this.props.activeTabKey

    if (activeKey && this._refs[activeKey]) {
      indicatorClass = this._refs[activeKey].props.indicatorClassName || null
      const activeEl = ReactDOM.findDOMNode(this._refs[activeKey])
      position = vertical ? activeEl.offsetTop : activeEl.offsetLeft
      size = vertical ? activeEl.offsetHeight : activeEl.offsetWidth
    } else {
      position = size = 0
    }

    if (indicatorClass !== this.state.indicatorClassName) {
      this.setState({ indicatorClassName: indicatorClass })
    }

    // line
    let transform = `translate${dir}(${position}px) scale${dir}(${size /
      INDICATOR_BASE_PX})`
    if (transform !== this.state.indicatorTransform) {
      this.setState({ indicatorTransform: transform })
    }

    // radio dot
    if (this.props.radio) {
      transform = `translate${dir}(${(size === 0 ? -5 : position) +
        size / 2}px) scale(${size === 0 ? 0 : 1})`
      if (transform !== this.state.radioTransform) {
        this.setState({ radioTransform: transform })
      }
    }
  }

  render() {
    const props = this.props
    const state = this.state
    const classes = cx(props.className, {
      pw_tabs: true,
      vertical: props.vertical,
      flex_tabs: props.flex,
      radio_tabs: props.radio
    })
    const elProps = _.omit(props, isDefinedCmpProp)

    return (
      <nav {...elProps} className={classes} role={props.radio ? 'radiogroup' : 'tablist'} id={this._id}>
        <ul className="nav nav-tabs" onKeyDown={this._onKeyDown}>
          {React.Children.map(props.children, this.processTabs)}
        </ul>
        <div
          className={`indicator active_indicator ${state.indicatorClassName}`}
          role="presentation"
          style={{
            WebkitTransform: state.indicatorTransform,
            transform: state.indicatorTransform
          }}
        />
        {props.radio
          ? <div
              className={`indicator radio_indicator ${state.indicatorClassName}`}
              role="presentation"
              style={{
                WebkitTransform: state.radioTransform,
                transform: state.radioTransform
              }}
            />
          : null}
      </nav>
    )
  }
}

export default DimensionAware(Tabs)
